import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import { toast } from "sonner";

import api, {
    formatApiError,
} from "@/lib/api";

import {
    useLang,
} from "@/contexts/LangContext";

import Layout from "@/components/Layout";

import {
    Button,
} from "@/components/ui/button";

import {
    Input,
} from "@/components/ui/input";


export default function AdminPayouts() {
    const {
        lang,
    } = useLang();

    const [
        payouts,
        setPayouts,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        references,
        setReferences,
    ] = useState({});

    const [
        payingId,
        setPayingId,
    ] = useState(null);


    const load = useCallback(
        async () => {
            try {
                const response =
                    await api.get(
                        "/admin/payouts"
                    );

                setPayouts(
                    response.data
                    || []
                );

            } catch (error) {
                toast.error(
                    formatApiError(
                        error.response
                            ?.data
                            ?.detail
                    )
                    || (
                        lang === "ar"
                            ? "\u062a\u0639\u0630\u0631 \u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u0645\u0633\u062a\u062d\u0642\u0627\u062a"
                            : "Could not load payouts"
                    )
                );

            } finally {
                setLoading(false);
            }
        },
        [lang]
    );


    useEffect(() => {
        load();
    }, [load]);


    const summary = useMemo(
        () => {
            const result = {
                pending: 0,
                available: 0,
                paid: 0,
            };

            payouts.forEach(
                (payout) => {
                    if (
                        Object.prototype
                            .hasOwnProperty
                            .call(
                                result,
                                payout.status
                            )
                    ) {
                        result[
                            payout.status
                        ] += Number(
                            payout.net_amount
                            || 0
                        );
                    }
                }
            );

            return result;
        },
        [payouts]
    );


    const statusLabel = (
        status
    ) => {
        const labels = {
            pending:
                lang === "ar"
                    ? "\u0645\u0639\u0644\u0642"
                    : "Pending",

            available:
                lang === "ar"
                    ? "\u0645\u062a\u0627\u062d \u0644\u0644\u062a\u062d\u0648\u064a\u0644"
                    : "Available",

            paid:
                lang === "ar"
                    ? "\u062a\u0645 \u0627\u0644\u062a\u062d\u0648\u064a\u0644"
                    : "Paid",

            cancelled:
                lang === "ar"
                    ? "\u0645\u0644\u063a\u064a"
                    : "Cancelled",
        };

        return (
            labels[status]
            || status
            || "-"
        );
    };


    const copyIban = async (
        iban
    ) => {
        if (!iban) {
            return;
        }

        try {
            await navigator.clipboard.writeText(
                iban
            );

            toast.success(
                lang === "ar"
                    ? "\u062a\u0645 \u0646\u0633\u062e \u0631\u0642\u0645 IBAN"
                    : "IBAN copied"
            );

        } catch {
            toast.error(
                lang === "ar"
                    ? "\u062a\u0639\u0630\u0631 \u0646\u0633\u062e IBAN"
                    : "Could not copy IBAN"
            );
        }
    };


    const markPaid = async (
        payout
    ) => {
        const reference = (
            references[
                payout.payout_id
            ]
            || ""
        ).trim();

        if (!reference) {
            toast.error(
                lang === "ar"
                    ? "\u0623\u062f\u062e\u0644 \u0631\u0642\u0645 \u0645\u0631\u062c\u0639 \u0627\u0644\u062a\u062d\u0648\u064a\u0644"
                    : "Enter the transfer reference"
            );

            return;
        }

        setPayingId(
            payout.payout_id
        );

        try {
            await api.post(
                `/admin/payouts/${payout.payout_id}/mark-paid`,
                {
                    transfer_reference:
                        reference,
                }
            );

            toast.success(
                lang === "ar"
                    ? "\u062a\u0645 \u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062a\u062d\u0648\u064a\u0644"
                    : "Payout marked as paid"
            );

            await load();

        } catch (error) {
            toast.error(
                formatApiError(
                    error.response
                        ?.data
                        ?.detail
                )
                || (
                    lang === "ar"
                        ? "\u062a\u0639\u0630\u0631 \u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062a\u062d\u0648\u064a\u0644"
                        : "Could not mark payout as paid"
                )
            );

        } finally {
            setPayingId(null);
        }
    };


    const money = (value) =>
        Number(
            value || 0
        ).toFixed(2);


    if (loading) {
        return (
            <Layout>
                <div className="min-h-[50vh] grid place-items-center text-muted-foreground">
                    ...
                </div>
            </Layout>
        );
    }


    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">

                <div className="flex items-end justify-between gap-4 flex-wrap">

                    <div>
                        <div className="text-xs text-terracotta font-bold uppercase tracking-[0.2em]">
                            {lang === "ar"
                                ? "\u0627\u0644\u0645\u0627\u0644\u064a\u0629"
                                : "Finance"}
                        </div>

                        <h1 className="text-3xl md:text-4xl font-black text-espresso mt-1">
                            {lang === "ar"
                                ? "\u0645\u0633\u062a\u062d\u0642\u0627\u062a \u0627\u0644\u0628\u0627\u0626\u0639\u064a\u0646"
                                : "Seller payouts"}
                        </h1>
                    </div>

                    <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={load}
                    >
                        {lang === "ar"
                            ? "\u062a\u062d\u062f\u064a\u062b"
                            : "Refresh"}
                    </Button>

                </div>


                <div className="grid sm:grid-cols-3 gap-4 mt-8">

                    <SummaryCard
                        label={
                            lang === "ar"
                                ? "\u0645\u0639\u0644\u0642"
                                : "Pending"
                        }
                        value={`${money(summary.pending)} SAR`}
                    />

                    <SummaryCard
                        label={
                            lang === "ar"
                                ? "\u0645\u062a\u0627\u062d \u0644\u0644\u062a\u062d\u0648\u064a\u0644"
                                : "Available"
                        }
                        value={`${money(summary.available)} SAR`}
                    />

                    <SummaryCard
                        label={
                            lang === "ar"
                                ? "\u062a\u0645 \u062a\u062d\u0648\u064a\u0644\u0647"
                                : "Paid"
                        }
                        value={`${money(summary.paid)} SAR`}
                    />

                </div>


                <div className="mt-8 bg-white border border-border rounded-2xl overflow-hidden">

                    <div className="overflow-x-auto">

                        <table className="w-full text-sm">

                            <thead className="bg-surface/60 text-muted-foreground text-xs">
                                <tr>
                                    <th className="text-start p-3">
                                        {lang === "ar"
                                            ? "\u0627\u0644\u0628\u0627\u0626\u0639"
                                            : "Seller"}
                                    </th>

                                    <th className="text-start p-3">
                                        {lang === "ar"
                                            ? "\u0627\u0644\u0637\u0644\u0628"
                                            : "Order"}
                                    </th>

                                    <th className="text-start p-3">
                                        {lang === "ar"
                                            ? "\u0627\u0644\u0628\u0646\u0643"
                                            : "Bank account"}
                                    </th>


                                    <th className="text-start p-3">
                                        {lang === "ar"
                                            ? "\u0627\u0644\u0645\u0628\u064a\u0639\u0627\u062a"
                                            : "Sales"}
                                    </th>

                                    <th className="text-start p-3">
                                        {lang === "ar"
                                            ? "\u0627\u0644\u0639\u0645\u0648\u0644\u0629"
                                            : "Commission"}
                                    </th>

                                    <th className="text-start p-3">
                                        {lang === "ar"
                                            ? "\u0627\u0644\u0635\u0627\u0641\u064a"
                                            : "Net"}
                                    </th>

                                    <th className="text-start p-3">
                                        {lang === "ar"
                                            ? "\u0627\u0644\u062d\u0627\u0644\u0629"
                                            : "Status"}
                                    </th>

                                    <th className="text-start p-3">
                                        {lang === "ar"
                                            ? "\u0627\u0644\u062a\u062d\u0648\u064a\u0644"
                                            : "Transfer"}
                                    </th>
                                </tr>
                            </thead>


                            <tbody>

                                {payouts.map(
                                    (payout) => (

                                    <tr
                                        key={
                                            payout.payout_id
                                        }
                                        className="border-t border-border/60 align-top"
                                    >

                                        <td className="p-3">
                                            <div className="font-bold text-espresso">
                                                {
                                                    payout.seller_name
                                                }
                                            </div>

                                            <div className="text-xs text-muted-foreground">
                                                {
                                                    payout.seller_email
                                                }
                                            </div>
                                        </td>


                                        <td className="p-3 font-mono text-xs">
                                            {
                                                payout.order_id
                                            }
                                        </td>

                                        <td className="p-3 min-w-[280px]">

                                            {payout.iban ? (

                                                <div className="space-y-1.5">

                                                    <div className="font-bold text-espresso">
                                                        {
                                                            payout.bank_name
                                                            || "-"
                                                        }
                                                    </div>

                                                    <div className="text-xs text-muted-foreground">
                                                        {
                                                            payout.bank_account_name
                                                            || "-"
                                                        }
                                                    </div>

                                                    <div className="flex items-center gap-2 flex-wrap">

                                                        <code
                                                            dir="ltr"
                                                            className="text-xs bg-surface rounded-lg px-2 py-1 select-all"
                                                        >
                                                            {
                                                                payout.iban
                                                            }
                                                        </code>

                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-8 rounded-full"
                                                            onClick={
                                                                () =>
                                                                    copyIban(
                                                                        payout.iban
                                                                    )
                                                            }
                                                        >
                                                            {lang === "ar"
                                                                ? "\u0646\u0633\u062e"
                                                                : "Copy"}
                                                        </Button>

                                                    </div>

                                                </div>

                                            ) : (

                                                <span className="text-xs text-muted-foreground">
                                                    {lang === "ar"
                                                        ? "\u0644\u0627 \u062a\u0648\u062c\u062f \u0628\u064a\u0627\u0646\u0627\u062a \u0628\u0646\u0643\u064a\u0629"
                                                        : "No bank details"}
                                                </span>

                                            )}

                                        </td>


                                        <td className="p-3">
                                            {money(
                                                payout.products_sales
                                            )} SAR
                                        </td>


                                        <td className="p-3">
                                            {money(
                                                payout.commission_amount
                                            )} SAR
                                        </td>


                                        <td className="p-3 font-black text-terracotta">
                                            {money(
                                                payout.net_amount
                                            )} SAR
                                        </td>


                                        <td className="p-3">
                                            <span className="inline-flex rounded-full bg-surface px-2.5 py-1 text-xs font-bold">
                                                {
                                                    statusLabel(
                                                        payout.status
                                                    )
                                                }
                                            </span>
                                        </td>


                                        <td className="p-3 min-w-[230px]">

                                            {payout.status
                                                === "available" ? (

                                                <div className="flex gap-2">

                                                    <Input
                                                        value={
                                                            references[
                                                                payout.payout_id
                                                            ]
                                                            || ""
                                                        }
                                                        onChange={
                                                            (event) =>
                                                                setReferences(
                                                                    (
                                                                        current
                                                                    ) => ({
                                                                        ...current,
                                                                        [
                                                                            payout.payout_id
                                                                        ]:
                                                                            event.target.value,
                                                                    })
                                                                )
                                                        }
                                                        placeholder={
                                                            lang === "ar"
                                                                ? "\u0631\u0642\u0645 \u0645\u0631\u062c\u0639 \u0627\u0644\u062a\u062d\u0648\u064a\u0644"
                                                                : "Transfer reference"
                                                        }
                                                    />

                                                    <Button
                                                        className="bg-terracotta text-white whitespace-nowrap"
                                                        disabled={
                                                            payingId
                                                            === payout.payout_id
                                                        }
                                                        onClick={
                                                            () =>
                                                                markPaid(
                                                                    payout
                                                                )
                                                        }
                                                    >
                                                        {lang === "ar"
                                                            ? "\u062a\u0645 \u0627\u0644\u062a\u062d\u0648\u064a\u0644"
                                                            : "Mark paid"}
                                                    </Button>

                                                </div>

                                            ) : payout.status
                                                === "paid" ? (

                                                <div>
                                                    <div className="font-bold">
                                                        {
                                                            payout.transfer_reference
                                                            || "-"
                                                        }
                                                    </div>

                                                    <div className="text-xs text-muted-foreground">
                                                        {
                                                            payout.paid_at
                                                                ? new Date(
                                                                    payout.paid_at
                                                                ).toLocaleString(
                                                                    lang === "ar"
                                                                        ? "ar-SA"
                                                                        : "en-US"
                                                                )
                                                                : "-"
                                                        }
                                                    </div>
                                                </div>

                                            ) : (

                                                <span className="text-xs text-muted-foreground">
                                                    {payout.status
                                                        === "pending"
                                                        ? (
                                                            lang === "ar"
                                                                ? "\u0628\u0627\u0646\u062a\u0638\u0627\u0631 \u0627\u0644\u062a\u0648\u0635\u064a\u0644"
                                                                : "Waiting for delivery"
                                                        )
                                                        : "-"
                                                    }
                                                </span>
                                            )}

                                        </td>

                                    </tr>
                                ))}

                                {payouts.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan="8"
                                            className="p-10 text-center text-muted-foreground"
                                        >
                                            {lang === "ar"
                                                ? "\u0644\u0627 \u062a\u0648\u062c\u062f \u0645\u0633\u062a\u062d\u0642\u0627\u062a \u062d\u062a\u0649 \u0627\u0644\u0622\u0646"
                                                : "No payouts yet"}
                                        </td>
                                    </tr>
                                )}

                            </tbody>

                        </table>

                    </div>
                </div>

            </div>
        </Layout>
    );
}


function SummaryCard({
    label,
    value,
}) {
    return (
        <div className="bg-white border border-border rounded-2xl p-5">

            <div className="text-xs text-muted-foreground uppercase tracking-wide">
                {label}
            </div>

            <div className="text-2xl font-black text-espresso mt-2">
                {value}
            </div>

        </div>
    );
}
